import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { preconApi } from "../lib/api";
import { BOARD_STATUS_CHIP } from "./BidStatusChips";

const NO_CHANGE = "";

/** BID MASTER §4 — Settings › Sales › "Bid status → Pipeline stage" (editable, admin). */
function BoardSyncSwitch() {
  const [on, setOn] = useState(null);
  useEffect(() => { preconApi.getSettings().then((s) => setOn(!!s.board_pipeline_sync)).catch(() => setOn(false)); }, []);
  if (on === null) return null;
  const toggle = async (v) => {
    setOn(v);
    try { await preconApi.saveSettings({ board_pipeline_sync: v }); toast.success(v ? "Bid Board → Pipeline sync on" : "Bid Board → Pipeline sync off"); }
    catch (e) { setOn(!v); toast.error(e?.response?.data?.detail || "Not saved"); }
  };
  return (
    <label className="flex items-center gap-3 text-sm">
      <Switch checked={on} onCheckedChange={toggle} data-testid="settings-board-pipeline-sync-toggle" />
      <span>Bid Board → Pipeline sync <span className="text-xs text-muted-foreground">— off: the Bid Board never creates or changes Pipeline rows (stage, contact); the Pipeline comes from the Excel import and your edits. Pipeline → Bid Board (Won / Lost / Cancelled) always runs.</span></span>
    </label>
  );
}

export default function StatusMappingEditor() {
  const [data, setData] = useState(null);
  const load = () => preconApi.statusMapping().then(setData).catch(() => toast.error("Could not load the mapping table"));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const save = async (patch) => {
    const prev = data;
    setData((d) => ({ ...d, ...patch }));
    try { setData(await preconApi.putStatusMapping(patch)); toast.success("Mapping saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not saved"); setData(prev); }
  };
  const setForward = (i, k, v) => save({ forward: data.forward.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const setReverse = (i, k, v) => save({ reverse: data.reverse.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const chip = (s) => <span className={`rounded border px-1.5 py-0.5 text-[10px] ${BOARD_STATUS_CHIP[s.split(" · ")[0]] || "border-border"}`}>{s}</span>;
  const sel = (value, onChange, options, testid, allowNoChange) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="h-7 rounded border border-border bg-background px-1.5 text-xs">
      {allowNoChange && <option value={NO_CHANGE}>No change</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>);
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="settings-status-mapping">
      <h3 className="text-sm font-semibold text-foreground">Bid status → Pipeline stage <span className="text-xs font-normal text-muted-foreground">— board → pipeline on every change Qualify … Submitted and Reconcile (one-way, logged source bid_board); pipeline → board only for Won / Lost / Cancelled. "No change" writes nothing. A record is created only from New Bid / Started / Rebid / Budget onward.</span></h3>
      <BoardSyncSwitch />
      <div className="grid gap-6 md:grid-cols-2">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="py-1 text-left">Board status</th><th className="py-1 text-left">Pipeline stage</th><th className="py-1 text-left" title="When on, the board never overwrites a pipeline stage that differs">Pipeline wins</th></tr></thead>
          <tbody>{data.forward.map((r, i) => (
            <tr key={r.board_status} className="border-t border-border" data-testid={`mapping-forward-${r.board_status}`}>
              <td className="py-1.5 pr-3">{chip(r.board_status)}</td>
              <td className="py-1.5 pr-3">{sel(r.pipeline_stage || NO_CHANGE, (v) => setForward(i, "pipeline_stage", v), data.pipeline_values, `mapping-forward-stage-${r.board_status}`, true)}</td>
              <td className="py-1.5"><Checkbox checked={!!r.pipeline_wins} onCheckedChange={(v) => setForward(i, "pipeline_wins", !!v)} data-testid={`mapping-forward-wins-${r.board_status}`} /></td>
            </tr>))}
          </tbody>
        </table>
        <div className="space-y-3">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr><th className="py-1 text-left">Pipeline</th><th className="py-1 text-left">→ Board status</th><th className="py-1 text-left">Pipeline wins</th></tr></thead>
            <tbody>{data.reverse.map((r, i) => (
              <tr key={r.pipeline_stage} className="border-t border-border" data-testid={`mapping-reverse-${r.pipeline_stage}`}>
                <td className="py-1.5 pr-3 font-medium">{r.pipeline_stage}</td>
                <td className="py-1.5 pr-3 flex items-center gap-2">{sel(r.board_status, (v) => setReverse(i, "board_status", v), data.board_statuses, `mapping-reverse-status-${r.pipeline_stage}`)}{r.outcome && <span className="text-muted-foreground">outcome {r.outcome}</span>}</td>
                <td className="py-1.5"><Checkbox checked={!!r.pipeline_wins} onCheckedChange={(v) => setReverse(i, "pipeline_wins", !!v)} data-testid={`mapping-reverse-wins-${r.pipeline_stage}`} /></td>
              </tr>))}
            </tbody>
          </table>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={!!data.handoff_completes_reconcile} onCheckedChange={(v) => save({ handoff_completes_reconcile: v })} data-testid="settings-handoff-completes-reconcile" />
            <span>Handoff email completes reconcile <span className="text-xs text-muted-foreground">— the sent handoff email archives the bid as Won instead of the checklist (OFF by default)</span></span>
          </label>
        </div>
      </div>
    </section>
  );
}
